import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import {
  createPublicAssetDocumentsUrl,
  isFolderId,
  PublicAssetDetails,
  PublicAssetType,
} from "../../../helpers/publicAssetDocuments";

interface Props {
  assetType: PublicAssetType;
  folderId?: string;
  asset: Partial<Record<keyof PublicAssetDetails | "VIN", unknown>>;
}

const ShareAssetDocumentsButton = ({ assetType, folderId, asset }: Props) => {
  const [open, setOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">(
    "idle",
  );
  const url = folderId
    ? createPublicAssetDocumentsUrl(assetType, folderId, asset)
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ShareOutlinedIcon />}
        disabled={!isFolderId(folderId)}
        onClick={() => {
          setOpen(true);
          setCopyStatus("idle");
        }}
      >
        Share documents
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Share {assetType} documents</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>
              Anyone with this link can view the unit details and available
              documents without signing in.
            </Typography>
            <TextField
              label="Public document link"
              value={url}
              fullWidth
              multiline
              maxRows={4}
              InputProps={{ readOnly: true }}
              onFocus={(event) => event.target.select()}
            />
            <Typography variant="body2" color="text.secondary">
              Unit details are saved in the link. Create a new link after
              updating them. Documents are loaded when the link is opened.
            </Typography>
            {copyStatus === "copied" && (
              <Alert severity="success">Link copied.</Alert>
            )}
            {copyStatus === "error" && (
              <Alert severity="info">
                Select the link above and copy it manually.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open page
          </Button>
          <Button variant="contained" onClick={copyLink}>
            Copy link
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShareAssetDocumentsButton;
